import { Component, OnDestroy, inject, viewChild } from '@angular/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { Subject, takeUntil } from 'rxjs';

import { ApiKey } from '@rhapsody/ngx-api-key-client';
import { ChangeExpirationDialogComponent } from '@core/components/change-expiration/change-expiration-dialog.component';
import { CommonModule } from '@angular/common';
import { KeyService } from '@core/services/key.service';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideNativeDateAdapter } from '@angular/material/core';

@Component({
    selector: 'rh-dashboard',
    providers: [provideNativeDateAdapter()],
    imports: [
        CommonModule,
        MatButtonModule,
        MatDatepickerModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatGridListModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
    ],
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnDestroy {
  public accordion = viewChild.required(MatAccordion);
  public readonly keyService = inject(KeyService);
  readonly dialog = inject(MatDialog);
  private readonly onDestroy = new Subject();

  get listKeys$(): ApiKey[] {
    return this.keyService.filteredKeys();
  }
  public openPanels: number[] = [];

  public onPanelOpen(index: number) {
    if (!this.openPanels.includes(index)) {
      this.openPanels.push(index);
    }
  }

  public onPanelClose(index: number) {
    this.openPanels = this.openPanels.filter((i) => i !== index);
  }

  public isExpanded(index: number): boolean {
    return this.openPanels.includes(index);
  }

  public rowSpanTrigger(index: number) {
    return this.isExpanded(index) ? 9 : 1;
  }

  public openDialog(apiKey: ApiKey): void {
    const dialogRef = this.dialog.open(ChangeExpirationDialogComponent, {
      data: apiKey,
      height: '250px',
      width: '600px',
      panelClass: 'rh-expiration-change-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((result) => {
        if (result !== undefined) {
          this.keyService.patchKey(result);
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  public isArray() {
    return Array.isArray(this.keyService.listKeysResponse());
  }

  public isExpired(dateString: Date | undefined | null): boolean {
    try {
      if (dateString === null) return false;
      const date = new Date(dateString as string | number | Date);
      // Check if date is invalid
      if (isNaN(date.getTime())) return false;

      const now = new Date();
      return date < now;
    } catch {
      // Return false for any parsing errors
      return false;
    }
  }

  public isExpiring(
    dateString: Date | undefined | null,
    daysThreshold: number = 30
  ): boolean {
    try {
      if (dateString === null) return false;
      const date = new Date(dateString as string | number | Date);
      // Check if date is invalid
      if (isNaN(date.getTime())) return false;

      const now = new Date();
      const threshold = new Date();
      threshold.setDate(now.getDate() + daysThreshold);
      return date > now && date <= threshold;
    } catch {
      // Return false for any parsing errors
      return false;
    }
  }

  public convertData(data: ApiKey) {
    return JSON.stringify(data, null, 2);
  }

  public convertError(val: string) {
    const parseStr = JSON.parse(val);
    return JSON.stringify(parseStr, null, 2);
  }
}
